import React, { useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import KanbanColumn from './KanbanColumn';
import KanbanCard from './KanbanCard';
import './KanbanBoard.css';

const COLUMNS = [
  { id: 'pending', color: '#ff9800' },
  { id: 'preparing', color: '#2196F3' },
  { id: 'ready', color: '#A0F487' },
  { id: 'delivered', color: '#8C92A5' },
];

function KanbanBoard({ orders, onStatusChange, onCardClick, onCancelOrder, activeOrder, setActiveOrder, userRole }) {
  const { t } = useTranslation();

  // Configure sensors for both mouse/touch with activation constraints
  // This prevents accidental drags when scrolling
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms press delay for touch
        tolerance: 5, // 5px tolerance during delay
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Group orders by status
  const ordersByStatus = useMemo(() => {
    const grouped = {
      pending: [],
      preparing: [],
      ready: [],
      delivered: [],
    };

    orders.forEach((order) => {
      // Map delivery statuses to kitchen workflow
      // 'accepted' from delivery platforms = still pending for kitchen
      const effectiveStatus = order.status === 'accepted' ? 'pending' : order.status;

      if (grouped[effectiveStatus]) {
        grouped[effectiveStatus].push(order);
      }
    });

    // Sort by createdAt within each column (oldest first for urgency)
    Object.keys(grouped).forEach((status) => {
      grouped[status].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    });

    return grouped;
  }, [orders]);

  const handleDragStart = (event) => {
    const { active } = event;
    const order = orders.find((o) => o.orderId === active.id);
    setActiveOrder(order);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveOrder(null);

    if (!over) return;

    const orderId = active.id;
    const order = orders.find((o) => o.orderId === orderId);

    // Determine target column
    let targetStatus = over.id;

    // If dropped on a card, get the column from that card's order
    if (!COLUMNS.find((col) => col.id === over.id)) {
      const targetOrder = orders.find((o) => o.orderId === over.id);
      if (targetOrder) {
        targetStatus = targetOrder.status;
      }
    }

    // Only update if status changed and target is valid
    if (order && order.status !== targetStatus && COLUMNS.find((col) => col.id === targetStatus)) {
      // Prevent moving backwards in status (optional - can be removed if wanted)
      const currentIndex = COLUMNS.findIndex((col) => col.id === order.status);
      const targetIndex = COLUMNS.findIndex((col) => col.id === targetStatus);

      // Allow moving forward only (pending -> preparing -> ready -> delivered)
      if (targetIndex > currentIndex) {
        onStatusChange(orderId, targetStatus);
      }
    }
  };

  const handleDragCancel = () => {
    setActiveOrder(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      accessibility={{
        screenReaderInstructions: {
          draggable: t('dnd.instructions'),
        },
        announcements: {
          onDragStart: ({ active }) => t('dnd.dragStart', { id: active.id }),
          onDragOver: ({ active, over }) => over
            ? t('dnd.dragOver', { id: active.id, target: over.id })
            : t('dnd.dragOutside', { id: active.id }),
          onDragEnd: ({ active, over }) => over
            ? t('dnd.dragEnd', { id: active.id, target: over.id })
            : t('dnd.dragCancel', { id: active.id }),
          onDragCancel: ({ active }) => t('dnd.dragCancel', { id: active.id }),
        },
      }}
    >
      <div className="kanban-board">
        {COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            id={column.id}
            title={t(`orders.status.${column.id}`)}
            color={column.color}
            orders={ordersByStatus[column.id]}
            count={ordersByStatus[column.id].length}
            onStatusChange={onStatusChange}
            onCardClick={onCardClick}
            onCancelOrder={onCancelOrder}
            isDelivered={column.id === 'delivered'}
            userRole={userRole}
          />
        ))}
      </div>

      {/* Drag overlay for smooth dragging visual */}
      <DragOverlay>
        {activeOrder ? <KanbanCard order={activeOrder} isDragging userRole={userRole} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

export default KanbanBoard;
